import SendTicketsToEmailContent from '@/components/manage/tickets/sendTickets/SendTicketsToEmailContent';
import SendTicketsToEmailFooter from '@/components/manage/tickets/sendTickets/SendTicketsToEmailFooter';
import { Form } from '@/components/primitives/Form';
import ResponsiveDialog from '@/components/ResponsiveDialog';
import { sendTicketsToEmail } from '@/features/manage/manageActions';
import { manageFlowBookingSelector } from '@/features/manage/manageSelectors';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { SendToEmailFormValues } from '@/types/sendTicketsToEmail';
import { createSendTicketsToEmailSchema } from '@/utils/zodSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

interface SendTicketsToEmailModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

const SendTicketsToEmailModal: FC<SendTicketsToEmailModalProps> = ({
  isOpen,
  onOpenChange,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const booking = useSelector(manageFlowBookingSelector)!;

  const defaultValues = useMemo<SendToEmailFormValues>(() => {
    return {
      bookingId: `${booking.id}`,
      passengers: [],
      purchaser: {
        sendSms: false,
        sendEmail: true,
        emailOverride: [
          `${booking.purchaser?.contactInformation?.emailAddress}`,
        ],
      },
      includeTaxInvoices: true,
      _meta: {
        sendAllTickets: true,
      },
    };
  }, [booking]);

  const form = useForm<SendToEmailFormValues>({
    mode: 'onChange',
    resolver: zodResolver(createSendTicketsToEmailSchema(t)),
    defaultValues,
  });

  const handleClose = useCallback(() => {
    onOpenChange(false);
    form.reset(defaultValues);
  }, [onOpenChange, form, defaultValues]);

  const onSubmit = useCallback(
    async ({ _meta, ...payload }: SendToEmailFormValues) => {
      if (!_meta.sendAllTickets && !payload.passengers?.length) {
        toast.error(<TransText i18nKey="recipientMustBeChosen" />);
        return;
      }
      await dispatch(sendTicketsToEmail(payload))
        .unwrap()
        .then(() => {
          const allTicketsCount =
            _meta.sendAllTickets && payload.purchaser.emailOverride.length;
          const personalTicketsCount = payload.passengers?.length;

          const successMessages = [];
          allTicketsCount &&
            successMessages.push(
              t('text.allTicketsCount', {
                count: allTicketsCount,
                postProcess: 'interval',
              })
            );
          personalTicketsCount &&
            successMessages.push(
              t('text.personalTicketsCount', {
                count: personalTicketsCount,
                postProcess: 'interval',
              })
            );

          toast.success(successMessages.join(', '));
          handleClose();
        });
    },
    [dispatch, handleClose, t]
  );

  const formId = useMemo(() => 'send-to-email-form', []);

  return (
    <FormProvider {...form}>
      <Form form={form} onSubmit={form.handleSubmit(onSubmit)} id={formId}>
        <ResponsiveDialog
          open={isOpen}
          onOpenChange={onOpenChange}
          title={<TransText i18nKey="sendToEmail" />}
          content={<SendTicketsToEmailContent />}
          footer={
            <SendTicketsToEmailFooter onClose={handleClose} formId={formId} />
          }
        />
      </Form>
    </FormProvider>
  );
};

export default SendTicketsToEmailModal;
